import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { UserFileRead } from '@/uppy-file-upload/schemas'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { FileListRequestState } from './slice'
import { isLocalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'

const IDLE_FILE_LIST_REQUEST: FileListRequestState = { status: 'idle', error: null }

export function isExternalStorageLinkedFile(file: Pick<UserFileRead, 'external_storage'>): boolean {
  return !isLocalStorageItem(file)
}

const selectShouldShowExternalStorageItemsInDrive = (_state: RootState): boolean => kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SHOW_EXTERNAL_STORAGE_ITEMS)

// ============================================================================
// Base Selectors (raw state access)
// ============================================================================

/** Ordered file ids per folder (references into `filesById`). */
export const selectFilesByFolderId = (state: RootState) => state.userFilesState.filesByFolderId

export const selectFileListRequestsByFolderId = (state: RootState) => state.userFilesState.fileListRequestsByFolderId

/** Every known file record, keyed by id. Single source of truth. */
export const selectFilesById = (state: RootState) => state.userFilesState.filesById

/** All known file records keyed by id. Alias of `selectFilesById`. */
export const selectLoadedUserFilesById = selectFilesById

// ============================================================================
// Current Folder Files Selectors
// ============================================================================

function resolveFolderFiles(
  filesByFolderId: Record<string, string[]>,
  filesById: Record<string, UserFileRead>,
  folderId: string | null | undefined
): UserFileRead[] {
  if (!folderId) return []
  const ids = filesByFolderId[folderId]
  if (!ids) return []
  return ids.map((id) => filesById[id]).filter((file): file is UserFileRead => Boolean(file))
}

/**
 * Select files for a specific folder.
 * These are the backend-filtered browse rows currently loaded for that folder.
 */
export const selectFilesForFolder = createSelector(
  [selectFilesByFolderId, selectFilesById, (_state: RootState, props: { folderId: string | null | undefined }) => props.folderId],
  (filesByFolderId, filesById, folderId): UserFileRead[] => resolveFolderFiles(filesByFolderId, filesById, folderId)
)

/**
 * Select files for the current folder.
 * These are the backend-filtered browse rows currently loaded for that folder.
 */
export const selectCurrentFolderFiles = createSelector(
  [selectFilesByFolderId, selectFilesById, selectCurrentFolderId],
  (filesByFolderId, filesById, currentFolderId): UserFileRead[] => resolveFolderFiles(filesByFolderId, filesById, currentFolderId)
)

/**
 * Drive-visible files for the current folder.
 * Raw selectors intentionally keep external-storage records for pickers and other consumers.
 */
export const selectCurrentFolderDriveVisibleFiles = createSelector(
  [selectCurrentFolderFiles, selectShouldShowExternalStorageItemsInDrive],
  (files, showExternalStorageItems): UserFileRead[] => {
    if (showExternalStorageItems) return files
    return files.filter((file) => !isExternalStorageLinkedFile(file))
  }
)

/**
 * Select whether files are currently loading for the current folder
 */
export const selectIsCurrentFolderFilesLoading = createSelector(
  [selectFileListRequestsByFolderId, selectCurrentFolderId],
  (fileListRequestsByFolderId, currentFolderId): boolean => {
    if (!currentFolderId) return false
    return fileListRequestsByFolderId[currentFolderId]?.status === 'loading'
  }
)

export const selectFileListRequest = (state: RootState, props: { folderId: string | null | undefined }): FileListRequestState => {
  if (!props.folderId) return IDLE_FILE_LIST_REQUEST
  return state.userFilesState.fileListRequestsByFolderId[props.folderId] ?? IDLE_FILE_LIST_REQUEST
}

/**
 * File ids in the current folder that still need folder-owned polling.
 * Folder state only owns `PROCESSING` rows; `PENDING` remains Uppy-owned.
 */
export const selectCurrentFolderProcessingFileIds = createSelector([selectCurrentFolderFiles], (files): string[] =>
  files.filter((file) => file.processing_status === UserFileProcessingStatus.Processing).map((file) => file.id)
)
